import { Button, Card, Col, Divider, Form, Input, InputNumber, Row, Select, Space, Switch, Typography, notification } from 'antd';
import React, { useEffect, useState } from 'react';
import { CompanyModel, CompanyStatus } from '../../../types/admin';
import { Timestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase-config';

const DEFAULT_AMAZON_CONTENT_SETTINGS = {
    titleMaxChars: 200,
    bulletPointCount: 5,
    descriptionMaxChars: 2000,
};

interface GeneralSettingsTabProps {
    company: CompanyModel;
    canManage: boolean;
}

const GeneralSettingsTab: React.FC<GeneralSettingsTabProps> = ({
    company,
    canManage,
}) => {
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        form.setFieldsValue({
            name: company.name ?? '',
            status: company.status ?? CompanyStatus.CREATED,
            modules: {
                shopify: Boolean(company.modules?.shopify),
                customContent: Boolean(company.modules?.customContent),
                generation: Boolean(company.modules?.generation),
                brandResearch: Boolean(company.modules?.brandResearch),
            },
            amazonContentSettings: {
                ...DEFAULT_AMAZON_CONTENT_SETTINGS,
                ...(company.amazonContentSettings ?? {}),
            },
        });
    }, [company, form]);

    const handleSave = async (values: any) => {
        try {
            setSaving(true);
            await updateDoc(doc(db, 'companies', company.id), {
                name: values.name,
                status: values.status,
                modules: {
                    shopify: Boolean(values.modules?.shopify),
                    customContent: Boolean(values.modules?.customContent),
                    generation: Boolean(values.modules?.generation),
                    brandResearch: Boolean(values.modules?.brandResearch),
                },
                amazonContentSettings: {
                    titleMaxChars: Number(values.amazonContentSettings?.titleMaxChars ?? DEFAULT_AMAZON_CONTENT_SETTINGS.titleMaxChars),
                    bulletPointCount: Number(values.amazonContentSettings?.bulletPointCount ?? DEFAULT_AMAZON_CONTENT_SETTINGS.bulletPointCount),
                    descriptionMaxChars: Number(values.amazonContentSettings?.descriptionMaxChars ?? DEFAULT_AMAZON_CONTENT_SETTINGS.descriptionMaxChars),
                },
                updatedAt: Timestamp.now(),
            });
            notification.success({
                message: 'Company updated',
                description: 'Settings saved successfully.',
            });
        } catch (error: any) {
            notification.error({
                message: 'Failed to update company',
                description: error?.message || String(error),
            });
        } finally {
            setSaving(false);
        }
    };

    if (!canManage) {
        return (
            <Card>
                <Typography.Text type="secondary">
                    You do not have permission to manage settings.
                </Typography.Text>
            </Card>
        );
    }

    return (
        <Card title="Company Settings" bordered={false}>
            <Form
                layout="vertical"
                form={form}
                onFinish={handleSave}
            >
                <Row gutter={16}>
                    <Col xs={24} md={16}>
                        <Form.Item
                            label="Name"
                            name="name"
                            rules={[{ required: true, message: 'Please enter a name.' }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Status" name="status">
                            <Select
                                options={[
                                    { value: CompanyStatus.CREATED, label: 'created' },
                                    { value: CompanyStatus.HIDDEN, label: 'hidden' },
                                    { value: CompanyStatus.DELETE, label: 'delete' },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item label="Modules">
                    <Space size="large" wrap>
                        <Form.Item name={['modules', 'shopify']} valuePropName="checked" noStyle>
                            <Switch checkedChildren="Shopify" unCheckedChildren="Shopify" />
                        </Form.Item>
                        <Form.Item name={['modules', 'customContent']} valuePropName="checked" noStyle>
                            <Switch checkedChildren="Custom Content" unCheckedChildren="Custom Content" />
                        </Form.Item>
                        <Form.Item name={['modules', 'generation']} valuePropName="checked" noStyle>
                            <Switch checkedChildren="Generation" unCheckedChildren="Generation" />
                        </Form.Item>
                        <Form.Item name={['modules', 'brandResearch']} valuePropName="checked" noStyle>
                            <Switch checkedChildren="Brand Research" unCheckedChildren="Brand Research" />
                        </Form.Item>
                    </Space>
                </Form.Item>
                <Divider />
                <Typography.Title level={5}>Amazon Content Preferences</Typography.Title>
                <Row gutter={16}>
                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Title max length"
                            name={['amazonContentSettings', 'titleMaxChars']}
                            rules={[{ required: true, message: 'Please enter a title length.' }]}
                        >
                            <InputNumber min={50} max={200} addonAfter="chars" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Bullet point count"
                            name={['amazonContentSettings', 'bulletPointCount']}
                            rules={[{ required: true, message: 'Please enter a bullet point count.' }]}
                        >
                            <InputNumber min={1} max={10} addonAfter="bullets" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Description max length"
                            name={['amazonContentSettings', 'descriptionMaxChars']}
                            rules={[{ required: true, message: 'Please enter a description length.' }]}
                        >
                            <InputNumber min={500} max={2000} step={100} addonAfter="chars" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={saving}>
                        Save
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default GeneralSettingsTab;
